<template>
  <div>
    <div class="level">
      <div class="level-left">
        <div class="columns">
          <div v-if="!detail" class="column is-narrow">
            <selecteur-action
              :options="[]"
              :placeholder='$t("general.placeHolder.groupedAction")'
              :elementsSelectionnes="false"
              titleButton="general.button.apply">
            </selecteur-action>
          </div>
          <form
            @submit.prevent="criteresRecherche.auto = search"
            :class="{'is-4': !detail}"
            class="column pdr-0">
            <b-input
              v-model="search"
              :placeholder="$t('general.autocomplete.search')"
              type="search" icon="magnify">
            </b-input>
          </form>
          <div class="column is-narrow pdr-0">
            <button
              @click="setShowFiltre"
              class="button">
              <b-icon icon="filter-variant" size="is-small" type="is-default"/>
              <span class="is-size-7">
                {{$t('general.button.filtre')}}
                <label
                  class="has-text-weight-bold"
                  :class="nombreCritere > 0 ? '' : 'is-invisible'">
                  ({{ nombreCritere }})
                </label>
              </span>
            </button>
            <span
              class="info-circle-right is-pulled-right is-cursor-pointer"
              @click="reinitialiserFiltre()">
              <b-icon icon="close-circle" type="is-danger"/>
            </span>
          </div>
        </div>
      </div>
      <div class="level-right">
        <dropdownButton
          type="is-addOrEdit"
          :permission="permCatalogue.maj"
          :actions="actionSecondary"
          @click="() => {
            $router.push({
                  name: 'm.bodac.catalogue-nouveauPresse',
                  params: { marcheId: $route.params.id },
                });
          }">
            {{ $t('addEditMarche.produits.newProductPresse') }}
        </dropdownButton>
      </div>
    </div>
    <v-client-table
      :data="produitsFilter"
      :columns="columns"
      :options="options"
      ref="tableProduits">
      <div slot="selected" slot-scope="props" class="has-text-centered">
        <qd-checkbox
          v-model="props.row.selected"
          :value="props.row"
          @input="selectAll()">
        </qd-checkbox>
      </div>
      <div slot="reference" slot-scope="props">
        <router-link-blank
          v-if="props.row.regroupementProduitId"
          :to="{
            name: 'c.bodac.catalogue-detailRegroupementProduit',
            params: {id: props.row.regroupementProduitId}
          }"
          :message="props.row.reference"
          :class="{'text-access-no-permission':
            !checkPermissions(permissions.lecture)}"
          class="lien">
        </router-link-blank>
        <router-link-blank
          v-else
          :to="{
            name: getLibelleAndRoutesForProduct(props.row).routeDetail,
            params: {id: props.row.id}
          }"
          :message="props.row.reference"
          :class="{'text-access-no-permission':
            !checkPermissions(permissions.lecture)}"
          class="lien">
        </router-link-blank>
      </div>
      <div slot="type" slot-scope="props">
        <div v-if="props.row.regroupementProduitId">
          {{ $t('catalogue.type.regroupementProduit') }}
        </div>
        <div v-else>{{ $t(getLibelleAndRoutesForProduct(props.row).libelle) }}</div>
      </div>
      <div slot="agence" slot-scope="props">
        <div>{{ getAgenceLibelle(props.row.agenceId) }}</div>
      </div>
      <div slot="societe" slot-scope="props">
        <div>{{ getLibelleSociete(props.row.supportCommercial.societeId) }}</div>
      </div>
      <div slot="regroupementProduit" slot-scope="props" class="has-text-centered">
        <span v-if="props.row.regroupementProduit">
          <a class="lien" @click="showPopupRegroupement(props.row.regroupementProduit)">
            <b-icon icon="eye" type="is-default"/>
          </a>
        </span>
      </div>
      <div slot="actif" slot-scope="props">
        <div>{{ getLibelleActif(props.row.actif) }}</div>
      </div>
      <template slot='afterTable'>
        <table-items-in-view @input='$refs.tableProduits.setLimit($event)' />
      </template>
    </v-client-table>
    <transition
      :duration="10"
      name="custom-classes-transition"
      enter-active-class="animate__animated animate__slideInRight"
      leave-active-class="animate__animated animate__slideOutRight">
      <div v-show="showFiltre" class="componentBrief">
        <filter-produits
          ref="filtreProduits"
          @close="makeRechercheFilter"
          @toggleFiltre="setShowFiltre"/>
      </div>
    </transition>
    <b-modal
      v-model="popupRegroupementProduit"
      has-modal-card
      class="modal">
      <popup-regroupement-produits
        v-if="regroupementProduitSelected"
        :regroupement-id="regroupementProduitSelected.id"
      />
    </b-modal>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import { SET_LOCK_MENU } from '@/store/modules/societe-mutation-type';
import {
  NO_PER_PAGE_VALUE,
  RESULTS_PER_PAGE,
  SORT_ICON,
  TEXTS,
} from '@/configuration/clientTableConf';
import TableItemsInView from '@/components/general/TableItemsInView';
import dropdownButton from '@/components/general/button/dropdownButton';
import routerLinkBlank from '@/components/general/routerLinkBlank';
import selecteurAction from '@/components/general/selecteurAction';
import FilterProduits from '@/components/achat/marche/produits/filterProduits';
import PopupRegroupementProduits from '@/components/achat/marche/produits/p_regroupementProduits';
import { PLAN_MEDIA } from '@/configuration/presse/permissionsPresseConf';
import * as produitConf from '@/configuration/catalogue/produitConf';
import { CATALOGUE } from '@/configuration/permissionsBodacConf';
import calculNombreCritere from '@/services/general/calculNombreCritere';
import { columnsSplice } from '@/services/general/columns';

const Permissions = createNamespacedHelpers('permissions');
const Marche = createNamespacedHelpers('marche');
const Societe = createNamespacedHelpers('societe');

export default {
  name: 'MarcheProduits',
  props: {
    marche: {
      type: Object,
      required: true,
    },
    detail: {
      type: Boolean,
    },
  },
  components: {
    TableItemsInView,
    dropdownButton,
    routerLinkBlank,
    selecteurAction,
    FilterProduits,
    PopupRegroupementProduits,
  },
  data() {
    return {
      showFiltre: false,
      popupRegroupementProduit: false,
      regroupementProduitSelected: null,
      search: '',
      permissions: PLAN_MEDIA,
      permCatalogue: CATALOGUE,
      productsAdd: [
        { nameRoute: 'm.bodac.catalogue-nouveauAutre', label: 'addEditMarche.produits.newProductAutre' },
        { nameRoute: 'm.bodac.catalogue-nouveauSalon', label: 'addEditMarche.produits.newProductSalon' },
        { nameRoute: 'm.bodac.catalogue-nouveauSponsoring', label: 'addEditMarche.produits.newProductSponsoring' },
      ],
    };
  },
  computed: {
    ...Permissions.mapGetters({
      checkPermissions: 'checkPermissions',
    }),
    ...Marche.mapGetters({
      criteresRecherche: 'criteresRechercheProduits',
    }),
    ...Societe.mapGetters({
      lockMenu: 'lockMenu',
      societesInformations: 'societesInformations',
    }),
    nombreCritere() {
      const banCritere = ['auto', 'actif', 'avecLiaisons'];
      let nombre = calculNombreCritere(this.criteresRecherche, banCritere);
      nombre += this.criteresRecherche.actif === null ? 0 : 1;
      nombre += this.criteresRecherche.avecLiaisons === null ? 0 : 1;
      return nombre;
    },
    actionSecondary() {
      const actions = [];

      this.productsAdd.forEach(product => {
        actions.push({
          label: this.$t(product.label),
          action: () => {
            this.$router.push({
              name: product.nameRoute,
              params: {
                marcheId: this.$route.params.id,
              },
            });
          },
          permissionAction: PLAN_MEDIA.maj,
        });
      });

      return actions;
    },
    produits() {
      const produits = [];
      const reduce = this.marche.produits.reduce((acc, val) => {
        if (val.regroupementProduitId) {
          if (!acc[val.regroupementProduitId]) {
            acc[val.regroupementProduitId] = [{
              ...val,
              reference: val.regroupementProduit.code,
              libelle: val.regroupementProduit.libelle,
              supportCommercial: {
                ...val.supportCommercial,
                societeId: val.regroupementProduit.societeId,
              },
            }];
          }
        } else if (acc.autre) {
          acc.autre.push(val);
        } else acc.autre = [val];
        return acc;
      }, {});
      Object.values(reduce).forEach(el => {
        produits.push(...el);
      });
      return produits;
    },
    produitsFilter() {
      return this.produits.filter(item => {
        let search = false;
        [
          item.reference,
          item.libelle,
        ].forEach((itemSearch) => {
          if (itemSearch && itemSearch.toLowerCase()
            .includes(this.criteresRecherche.auto.toLowerCase())) {
            search = true;
          }
        });

        let categorie = this.criteresRecherche.categorie.length === 0;
        if (!categorie) {
          categorie = !!(this.criteresRecherche.categorie
            .find(c => item.supportCommercial.categorieVente.includes(c.code)));
        }

        let actif = this.criteresRecherche.actif === null;
        if (typeof this.criteresRecherche.actif === 'boolean') {
          actif = this.criteresRecherche.actif === item.actif;
        }

        let avecLiaisons = this.criteresRecherche.avecLiaisons === null;
        if (typeof this.criteresRecherche.avecLiaisons === 'boolean') {
          avecLiaisons = this.criteresRecherche.avecLiaisons
            ? item.liaisonsTarif.length > 0
            : item.liaisonsTarif.length === 0;
        }

        return search && categorie && actif && avecLiaisons;
      });
    },
    iconSelectTotal() {
      if (this.allItemsSelected) return 'checkbox-multiple-marked-outline';
      return 'checkbox-multiple-blank-outline';
    },
    allItemsSelected() {
      return this.getAllSelected().length === this.produitsFilter.length
        && this.produitsFilter.length > 0;
    },
    options() {
      const texts = cloneDeep(TEXTS);
      texts.count = '';
      return {
        sortIcon: SORT_ICON,
        headings: {
          reference: this.$i18n.t('addEditMarche.produits.tab.reference'),
          type: this.$i18n.t('addEditMarche.produits.tab.type'),
          libelle: this.$i18n.t('addEditMarche.produits.tab.label'),
          societe: this.$i18n.t('addEditMarche.produits.tab.societe'),
          agence: this.$i18n.t('addEditMarche.produits.tab.agence'),
          regroupementProduit: this.$i18n.t('addEditMarche.produits.tab.regroupementProduit'),
          actif: this.$i18n.t('addEditMarche.produits.tab.actif'),
          selected: (h) => {
            return h(
              'a',
              {
                attrs: {
                  class: 'pointer',
                },
                on: {
                  click: () => {
                    this.selectAll();
                  },
                },
              },
              [
                h('b-icon', {
                  attrs: {
                    icon: this.iconSelectTotal,
                  },
                }),
              ],
            );
          },
        },
        filterable: false,
        perPageValues: NO_PER_PAGE_VALUE,
        perPage: RESULTS_PER_PAGE,
        texts: texts,
        sortable: ['reference', 'libelle', 'type', 'actif'],
        columnsClasses: {
          selected: 'colonneSelected',
        },
      };
    },
    columns() {
      const columns = ['selected', 'reference', 'type', 'libelle', 'agence', 'societe', 'regroupementProduit', 'actif'];
      if (this.detail) {
        columnsSplice(columns, 'selected');
      }
      return columns;
    },
  },
  methods: {
    ...Societe.mapMutations({
      setLockMenu: SET_LOCK_MENU,
    }),
    ...Permissions.mapActions({
      showPopUpPermission: 'showPopup',
    }),
    getAllSelected() {
      return this.produitsFilter.filter((el) => el.selected);
    },
    selectAll() {
      const state = !this.allItemsSelected;
      this.produitsFilter.forEach(item => {
        item.selected = state;
      });
    },
    setShowFiltre() {
      this.showFiltre = !this.showFiltre;
      if (this.showFiltre) this.$refs.filtreProduits.initializeFiltre();
      this.setLockMenu(!this.lockMenu);
    },
    makeRechercheFilter() {
      if (this.showFiltre) this.setShowFiltre();
    },
    reinitialiserFiltre() {
      this.$refs.filtreProduits.initializeFiltre();
      this.$refs.filtreProduits.viderCritere();
      this.$refs.filtreProduits.appliquerCritere();
      this.showFiltre = false;
    },
    getLibelleAndRoutesForProduct(produit) {
      const produitConfiguration = produitConf.CATEGORIE_PRODUIT.find((c) => {
        return c.code === produit.supportCommercial.categorieVente;
      });
      if (!produitConfiguration) return '';
      if (produitConfiguration.code !== produitConf.CATEGORIE_LOCATION) return produitConfiguration;
      return produit.numerique ? produitConfiguration.numerique : produitConfiguration.nonNumerique;
    },
    getAgenceLibelle(agenceId) {
      let libelleAgence = '';
      this.societesInformations.forEach((societe) => {
        societe.agences.forEach((agence) => {
          if (agence.id === agenceId) libelleAgence = agence.libelle;
        });
      });
      return libelleAgence;
    },
    getLibelleSociete(societeId) {
      const societe = this.societesInformations.find((societe) => {
        return societe.id === societeId;
      });
      if (societe) return societe.libelle;
      return null;
    },
    getLibelleActif(actif) {
      if (actif) return this.$t('general.non');
      return this.$t('general.oui');
    },
    showPopupRegroupement(regroupement) {
      this.regroupementProduitSelected = regroupement;
      this.popupRegroupementProduit = true;
    },
  },
};
</script>
